import type { Market } from "../domain/location/types";

export interface MarketProfile {
	market: Market;
	locale: "sv-SE" | "en-US";
	appName: string;
}

const MARKET_PROFILES: Record<Market, MarketProfile> = {
	se: {
		market: "se",
		locale: "sv-SE",
		appName: "Bygg en bastu",
	},
	intl: {
		market: "intl",
		locale: "en-US",
		appName: "Sauna Creator",
	},
};

export function getMarketProfile(market: Market): MarketProfile {
	return MARKET_PROFILES[market];
}

function normalizeDefaultMarket(value?: string): Market | null {
	if (value === "se" || value === "intl") {
		return value;
	}

	return null;
}

export function resolveMarket(
	searchParams?: URLSearchParams,
	hostname?: string,
	configuredDefaultMarket?: string,
): Market {
	const queryOverride = searchParams?.get("market");
	if (queryOverride === "se" || queryOverride === "intl") {
		return queryOverride;
	}

	const envDefault = normalizeDefaultMarket(configuredDefaultMarket);
	if (envDefault) {
		return envDefault;
	}

	if (hostname?.endsWith("saunacreator.com")) {
		return "intl";
	}

	return "se";
}

export function resolveMarketProfile(
	searchParams?: URLSearchParams,
	hostname?: string,
	configuredDefaultMarket?: string,
): MarketProfile {
	return getMarketProfile(
		resolveMarket(searchParams, hostname, configuredDefaultMarket),
	);
}

/**
 * Read the market the server baked into the served document.
 *
 * Every prerendered page and the app-shell template carry the resolved market
 * on `<html data-market="…">`. Returns null when the marker is absent (e.g. a
 * stale or non-prerendered document).
 */
export function readBakedMarket(
	doc?: Pick<Document, "documentElement">,
): Market | null {
	return normalizeDefaultMarket(doc?.documentElement.dataset.market);
}

/**
 * Resolve the market on the CLIENT at hydration time.
 *
 * The server's market is encoded in the served HTML (`<html data-market>`); the
 * client MUST hydrate with that exact market. Re-deriving it from hostname/env can
 * disagree with what the server rendered and trigger a React #418 hydration
 * mismatch (server and client must produce identical markup). Only falls back to
 * runtime resolution when no marker is present (non-prerendered documents).
 */
export function resolveClientMarket(configuredDefaultMarket?: string): Market {
	const baked =
		typeof document !== "undefined" ? readBakedMarket(document) : null;
	if (baked) {
		return baked;
	}

	const search = typeof window !== "undefined" ? window.location.search : "";
	const hostname =
		typeof window !== "undefined" ? window.location.hostname : undefined;
	return resolveMarket(
		new URLSearchParams(search),
		hostname,
		configuredDefaultMarket,
	);
}
